import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirVendorType, SupplierRequestType } from '@econtractor/types';
import { useModal } from 'context/ModalContext';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { useToast } from 'context/ToastContext';
import { useNeedPay } from 'modules/admin';
import { openVendorFilterDlg, type SelectOption } from 'modules/product';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { safeIntParse } from 'utils/helpers/safeParsing';

interface UseCloneSupplierRequestParams {
    onClone: (vendorId: number, sourceRequestId: number) => void;
}

const useCloneSupplierRequest = ({ onClone }: UseCloneSupplierRequestParams) => {
    const { t } = useTranslation(['Vendors', 'Products', 'common']);
    const { showDlg } = useModal();
    const { localSettings } = useSettings();
    const { darkTheme } = useTheme();
    const { showToast } = useToast();
    const { needPay } = useNeedPay();

    const vendorOptions: SelectOption[] = useMemo(
        () =>
            ((localSettings?.DIRS?.Vendor || []))
                .filter((v) => Number(v.ID) > 0) 
                .map((v) => ({ value: Number(v.ID), label: String(v.vendorName || `#${v.ID}`) })),
        [localSettings?.DIRS?.Vendor],
    );

    const cloneRequest = (row: SupplierRequestType) => {
        if (needPay()) return;

        const sourceId = safeIntParse(row.ID);
        const defaultVendorId = safeIntParse(row.vendorId) || null;
        if (!sourceId) return;

        openVendorFilterDlg({
            showDlg,
            t: ((key: string, options?: Record<string, unknown>) => {
                const map: Record<string, string> = {
                    selectVendorFilterTitle: t('supplierRequests.selectVendorCloneTitle'),
                    selectVendorFilterLabel: t('supplierRequests.selectVendorLabel'),
                    selectVendorFilterHint: t('supplierRequests.selectVendorCloneHint'),
                    selectAllVendors: t('supplierRequests.selectVendorPlaceholder'),
                };
                if (map[key]) return map[key];
                return t(key as never, options as never);
            }) as typeof t,
            initialVendorId: defaultVendorId,
            vendorOptions,
            selectTheme: darkTheme ? selectStylesDark : selectStylesLigth,
            onApply: (vendorId) => {
                if (!vendorId) {
                    showToast(t('supplierRequests.messVendorRequired'), 'warning');
                    return;
                }
                onClone(vendorId, sourceId);
            },
        });
    };

    return { cloneRequest };
};

export default useCloneSupplierRequest;
